// --------------------
// Constants
// --------------------

import { transformEventBase } from "./transforEventScheduler";

const sectorUid = "1e090328-9f51-4dda-bc5b-ba0da90bbda8";

// --------------------
// Types
// --------------------

export type WorkforceInput = {
  employees?: Array<{ uid: string }>;
  contractors?: Array<{ uid: string }>;
  supervisors?: Array<{ uid: string }>;
};

export type EventSchedulerInput = {
  eventPreferences?: {
    eventConfig?: any;
  };
};

export type CreatedJobWithoutQuoteInput = {
  workforce?: WorkforceInput;
  eventScheduler?: EventSchedulerInput;
  scopeOfWork?: any;
  client?: { uid: string; coverageArea?: { id: number } };
  companyId?: number;
  alias?: string;
};

export type AdvancedScopeOfWork = {
  name: string;

  serviceNameOverride?: string | null;

  sectorsUid: string;
  categoryUid: string;
  typeOfPlaceUid: string;
  typeOfServiceUid: string;

  averagePeople: number;
  squareFootage: string;

  typeOfService: {
    costBias: number;
    baseRatePerHour: number;
  };

  frequencyType: string;
  travelBufferTimeInGeneral?: number;

  areas: any[];
  otherAreas: any[];
  additionalObjects?: any[];
  specialRequests?: any[];
  costOverride?: any;

  clientUid: string | null;
  coverageAreaId: number | null;
  companyId: number | null;
};

export type FormattedForCreatedJobWithOutQuoteOutput = {
  workforce: Array<{ uid: string; role: "employee" | "contractor" | "supervisor" }>;
  eventConfig: any | null;
  scopeOfWork: AdvancedScopeOfWork;
  alias: string;
  type: string;
};

// --------------------
// Helpers
// --------------------

/**
 * Removes square / squareFootages from areas
 */
const normalizeAreas = (areas: any[] = []) => {
  return areas.map((area) => {
    const { square, squareFootages, ...rest } = area;

    return {
      ...rest,
      quantity: Number(squareFootages?.[0]?.quantity ?? 0),
    };
  });
};

// --------------------
// Helper (ADVANCED ONLY)
// --------------------

const buildAdvancedScopeOfWork = (
  sector: any,
  clientUid: string | null,
  coverageAreaId: number | null,
  companyId: number | null,
): AdvancedScopeOfWork => {
  return {
    name: sector?.service?.name ?? "Advanced Service",

    serviceNameOverride: sector?.serviceNameOverride?.trim() || null,

    sectorsUid: sectorUid,
    categoryUid: sector?.category?.uid ?? "",
    typeOfPlaceUid: sector?.places?.uid ?? "",
    typeOfServiceUid: sector?.service?.uid ?? "",

    averagePeople: Number(sector?.averagePeople) || 0,
    squareFootage: sector?.squareft ?? "",

    typeOfService: {
      costBias: Number(sector?.service?.costBias) || 1,
      baseRatePerHour: Number(sector?.service?.baseRatePerHour) || 0,
    },

    frequencyType: sector?.frequencyType ?? "by_weekly",
    travelBufferTimeInGeneral: sector?.travelBufferTimeInGeneral ?? 0,

    areas: normalizeAreas(sector?.areas),
    otherAreas: normalizeAreas(sector?.otherAreas),

    additionalObjects: sector?.objects ?? sector?.additionalObjects ?? [],

    specialRequests: sector?.specialRequests ?? [],

    costOverride: sector?.costOverride ?? null,

    clientUid,
    coverageAreaId,
    companyId,
  };
};

// --------------------
// Main formatter (ADVANCED)
// --------------------

export const formatedForCreatedJobWithOutQuoteAdvanced = (
  data: CreatedJobWithoutQuoteInput,
  companyId: number,
  eventBase: any,
  type: string,
): FormattedForCreatedJobWithOutQuoteOutput => {
  const w = data?.workforce ?? {};

  const workforce: FormattedForCreatedJobWithOutQuoteOutput["workforce"] = [
    ...(w.employees ?? [])?.map((p) => ({ uid: p.uid, role: "employee" as const })),
    ...(w.contractors ?? [])?.map((p) => ({ uid: p.uid, role: "contractor" as const })),
    ...(w.supervisors ?? [])?.map((p) => ({ uid: p.uid, role: "supervisor" as const })),
  ].filter((p) => !!p.uid);

  const sector = data?.scopeOfWork ?? {};

  const scopeOfWork = buildAdvancedScopeOfWork(sector, data?.client?.uid ?? null, data?.client?.coverageArea?.id ?? null, companyId);

  return {
    workforce,
    eventConfig: eventBase,
    scopeOfWork,
    alias: data?.alias,
    type,
  };
};
